import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const Mukteshwar = () => {
  const tourData = {
    name: "Mukteshwar",
    duration: "02 Nights 03 Days",
    price: "₹ 4,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1755001521/o5zv6vkc38zkc6uvcwhg.avif",
    altitude: "7,500 ft",
    difficulty: "Easy",
    region: "Uttarakhand",
    highlights: ["Weekend getaway", "Kainchi Dham", "Mountain views"],
    bestTime: "March to November",
    description: "The Mukteshwar tour is a perfect weekend getaway in Uttarakhand that combines spiritual experiences with scenic beauty. This easy tour offers temple visits, adventure activities, and mountain views, making it ideal for families and adventure enthusiasts."
  };

  return (
    <>
      <Helmet>
        <title>Mukteshwar Tour - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Mukteshwar tour in Uttarakhand. Weekend getaway with temple visits and scenic beauty." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Weekend getaway with temple visits and scenic beauty
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This tour takes you to the beautiful hill station of Mukteshwar, 
                    offering a perfect blend of spiritual experiences and adventure. 
                    The journey includes temple visits, adventure activities, and scenic 
                    mountain views, making it an ideal weekend escape from city life.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Weekend getaway experience</li>
                    <li>Kainchi Dham Temple visit</li>
                    <li>Mukteshwar viewpoint</li>
                    <li>Mountain views</li>
                    <li>Adventure activities</li>
                    <li>Easy difficulty level</li>
                    <li>Perfect for families</li>
                    <li>Bonfire and music</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Departure for Mukteshwar</h4>
                    <ul>
                      <li>Departure from Delhi at night</li>
                      <li>Overnight Journey from Delhi to Mukteshwar</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Reach Mukteshwar & Visit Kainchi Dham</h4>
                    <ul>
                      <li>Reach Mukteshwar & have breakfast</li>
                      <li>After check-in into rooms, get fresh and up for Adventure Activities</li>
                      <li>Visit to Kainchi Dham Temple & Mukteshwar viewpoint</li>
                      <li>Back to property & enjoy evening snacks and tea</li>
                      <li>Later enjoy dinner at night with bonfire and light music</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Mukteshwar Exploration & Return</h4>
                    <ul>
                      <li>Wake up early morning and enjoy the beautiful sunrise view</li>
                      <li>Refresh & have breakfast</li>
                      <li>Visit to Mukteshwar viewpoint and surrounding areas</li>
                      <li>Adventure activities (ziplining, rock climbing)</li>
                      <li>Lunch at local restaurant</li>
                      <li>Evening departure to Delhi</li>
                      <li>We will reach Delhi by Early Morning</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Travel by AC & Comfortable Bus/Traveler</li>
                    <li>1 Night at Mukteshwar Camp-site</li>
                    <li>Meal: 1 Breakfast, 1 Dinner</li>
                    <li>Kainchi Dham temple visit</li>
                    <li>Mukteshwar viewpoint visit</li>
                    <li>Adventure Activities - Zip-line, Rock climbing (on your own expense)</li>
                    <li>Trip Coordinator</li>
                    <li>24x7 Customer Support</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>5% GST</li>
                    <li>Extra Drinks, Food, Tea and Snacks</li>
                    <li>Tickets for any sightseeing or extra activity outside the camp area</li>
                    <li>Travel Insurance and other benefits</li>
                    <li>Medical expenses (apart from first aid) and insurance of any kind</li>
                    <li>Tips, laundry, liquors, wines, mineral water, telephone charges, camera fee and items of personal nature</li>
                    <li>Anything which is not mentioned in inclusions</li>
                    <li>Cost escalation due any unforeseen reason like landslide, road closure, natural calamity</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>7,500 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Tour Distance:</strong>
                      <span>320 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Easy</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>March to November</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person</h4>
                  <ul>
                    <li><strong>QUAD SHARING:</strong> ₹ 4,999 + 5% GST</li>
                    <li><strong>TRIPLE SHARING:</strong> ₹ 5,499 + 5% GST</li>
                    <li><strong>DOUBLE SHARING:</strong> ₹ 5,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Delhi:</strong> ISBT Kashmere Gate b/w 09:00 PM to 10:00 PM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Weekend getaway experience</li>
                    <li>Kainchi Dham Temple visit</li>
                    <li>Mukteshwar viewpoint</li>
                    <li>Mountain views</li>
                    <li>Adventure activities</li>
                    <li>Easy difficulty level</li>
                    <li>Perfect for families</li>
                    <li>Bonfire and music</li>
                    <li>Trip coordinator support</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default Mukteshwar;
